import MaterialIcon from '@components/common/MaterialIcon';
import type { TFile } from '@conversed/shared/src/central/types/common.type';
import { formatFileSize, getFileAvatar } from '@helpers/fileHelper';
import { toastErrorTranslated, toastWarningTranslated } from '@helpers/toast';
import uploadFileIcon from '@images/upload-file-icon.svg';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FileDropZoneProps {
  accept: string[];
  fileSizeLimitMB?: number;
  maxFiles?: number;
  selectedFiles?: TFile[];
  avatar?: string;
  onFilesSelect?: (files: TFile[]) => void;
  onFileRemove?: (index: number) => void;
  className?: string;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  multiple?: boolean;
}

export const FileDropZone: React.FC<FileDropZoneProps> = ({
  accept,
  selectedFiles = [],
  avatar,
  onFilesSelect,
  onFileRemove,
  fileSizeLimitMB,
  maxFiles,
  className = '',
  fileInputRef,
  multiple = false,
}) => {
  const { t } = useTranslation();
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const localInputRef = useRef<HTMLInputElement>(null);
  const inputRef = fileInputRef ?? localInputRef;

  // NOTE: if you change this, then you need to update the socket maxHttpBufferSize in backend too.
  // Backend maxHttpBufferSize is 100MB, but due to base64 encoding (~33% overhead),
  // we limit raw file size to 85MB to stay under the 100MB socket limit
  const MAX_TOTAL_SIZE_MB = 85;
  const MAX_FILES_PER_UPLOAD = 200;

  const convertFileToTFile = useCallback((file: File): Promise<TFile> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        resolve({
          name: file.name,
          size: file.size,
          type: file.type,
          base64: base64,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const validateFile = useCallback(
    (file: File): boolean => {
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
      const mimeTypeAccepted = accept.includes(file.type);
      const extensionAccepted = accept.includes(fileExtension);

      if (!mimeTypeAccepted && !extensionAccepted) {
        toastErrorTranslated('data-sources.upload.invalid-file-type');
        return false;
      }

      if (fileSizeLimitMB && file.size > fileSizeLimitMB * 1024 * 1024) {
        toastErrorTranslated('data-sources.upload.invalid-file-size', {
          limit: fileSizeLimitMB,
        });
        return false;
      }

      return true;
    },
    [accept, fileSizeLimitMB],
  );

  const handleFiles = useCallback(
    async (files: FileList) => {
      // No files selected or no file handler
      if (!files.length || !onFilesSelect) {
        return;
      }

      const validFiles = Array.from(files).filter(validateFile);
      if (!validFiles?.length) {
        return;
      }

      // Check file count limit for multiple file uploads
      if (multiple) {
        const fileCountLimit = maxFiles ?? MAX_FILES_PER_UPLOAD;
        const totalFilesAfterAdd = selectedFiles.length + validFiles.length;

        if (totalFilesAfterAdd > fileCountLimit) {
          toastErrorTranslated('data-sources.upload.too-many-files', {
            limit: fileCountLimit,
            current: selectedFiles.length,
            adding: validFiles.length,
          });
          return;
        }
      }

      // Check total batch size including existing files
      const newFilesSize = validFiles.reduce((sum, file) => sum + file.size, 0);
      const existingFilesSize = selectedFiles.reduce(
        (sum, file) => sum + file.size,
        0,
      );
      const totalSize = existingFilesSize + newFilesSize;
      const totalSizeMB = totalSize / 1024 / 1024;

      if (multiple && totalSizeMB > MAX_TOTAL_SIZE_MB) {
        toastWarningTranslated('data-sources.upload.batch-too-large', {
          limit: MAX_TOTAL_SIZE_MB,
          current: totalSizeMB.toFixed(1),
        });
        return;
      }

      try {
        const tFiles = await Promise.all(
          validFiles.map((file) => convertFileToTFile(file)),
        );

        if (multiple) {
          // Check for duplicate file names
          const existingFileNames = new Set(
            selectedFiles.map((file) => file.name.toLowerCase()),
          );

          const newUniqueFiles: TFile[] = [];
          const duplicateFileNames: string[] = [];

          tFiles.forEach((file) => {
            const lowerCaseName = file.name.toLowerCase();
            if (existingFileNames.has(lowerCaseName)) {
              duplicateFileNames.push(file.name);
            } else {
              newUniqueFiles.push(file);
              existingFileNames.add(lowerCaseName);
            }
          });

          // Show warning toast if duplicates were found
          if (duplicateFileNames.length > 0) {
            if (duplicateFileNames.length === 1) {
              toastWarningTranslated(
                'data-sources.upload.duplicate-file-warning',
                {
                  fileName: duplicateFileNames[0],
                },
              );
            } else {
              toastWarningTranslated(
                'data-sources.upload.duplicate-files-warning',
                {
                  count: duplicateFileNames.length,
                },
              );
            }
          }

          // Only add unique files
          if (newUniqueFiles.length > 0) {
            onFilesSelect([...selectedFiles, ...newUniqueFiles]);
          }
        } else {
          // Replace with single file
          onFilesSelect([tFiles[0]]);
        }
      } catch (error) {
        console.error('Error converting files:', error);
      }
    },
    [
      multiple,
      onFilesSelect,
      validateFile,
      convertFileToTFile,
      selectedFiles,
      maxFiles,
    ],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const files = e.dataTransfer.files;
      handleFiles(files);
    },
    [handleFiles],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;

      if (files) {
        handleFiles(files);
      }

      // * ensure you can set the same file twice
      e.target.value = '';
    },
    [handleFiles],
  );

  const handleClick = useCallback(() => {
    const hasFiles = selectedFiles.length > 0;
    if (!hasFiles) {
      inputRef.current?.click();
    }
  }, [selectedFiles, inputRef]);

  const handleRemove = useCallback(
    (index: number) => {
      if (onFileRemove) {
        onFileRemove(index);
      }
      // Reset the file input value so the same file can be selected again
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [onFileRemove, inputRef],
  );

  const hasFiles = selectedFiles.length > 0;

  return (
    <div className={`relative w-full ${className}`}>
      <input
        ref={inputRef}
        type='file'
        accept={accept.join(',')}
        onChange={handleFileInputChange}
        className='hidden'
        multiple={multiple}
      />

      <div
        ref={dropZoneRef}
        className={`p-4 flex items-center justify-center text-center w-full relative border-2 border-dashed rounded-md text-gray-600 transition-all duration-200 min-h-[110px] h-full border-gray-200 hover:border-gray-400 hover:bg-gray-100 bg-white ${
          hasFiles ? 'cursor-default' : 'cursor-pointer'
        } ${isDragActive ? 'opacity-30' : ''}`}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        {!hasFiles ? (
          <div className='flex flex-col justify-center align-center w-full'>
            <img
              src={avatar || uploadFileIcon}
              className='w-[30px] h-[30px] mx-auto'
              alt='upload icon'
            />
            <p className='text-pico text-gray-500'>
              {t('data-sources.upload.drag-and-drop-or-click')}
            </p>
          </div>
        ) : multiple && selectedFiles.length > 0 ? (
          // List view for multiple files
          <div className='w-full'>
            <div className='flex items-center justify-start mb-2 gap-2'>
              <p className='text-tiny font-medium text-primary-base'>
                {selectedFiles.length}{' '}
                {t('data-sources.upload.files-selected', {
                  count: selectedFiles.length,
                })}
              </p>
              <MaterialIcon
                icon='add_circle'
                classes={['text-primary-base', 'text-sm', 'cursor-pointer']}
                onClick={(e: any) => {
                  e?.stopPropagation();
                  inputRef.current?.click();
                }}
              />
            </div>
            <div className='max-h-[150px] overflow-y-auto custom-scrollbar'>
              {selectedFiles.map((file, index) => (
                <div key={`${file.name}-${index}`}>
                  <div className='flex items-stretch justify-between gap-2 py-1'>
                    <img
                      src={getFileAvatar(file.type, file.name, avatar)}
                      className='w-6 h-full flex-shrink-0 self-stretch object-contain my-auto'
                      alt={file.name}
                    />
                    <div className='flex-1 min-w-0 flex flex-col justify-center text-left'>
                      <p
                        className='text-tiny text-gray-800 truncate leading-tight'
                        title={file.name}>
                        {file.name}
                      </p>
                      <p className='text-timestamp text-gray-500 leading-tight'>
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <MaterialIcon
                      icon='close'
                      classes={[
                        'text-red-500',
                        'text-sm',
                        'cursor-pointer',
                        'flex-shrink-0',
                        'flex',
                        'items-center',
                      ]}
                      onClick={(e: any) => {
                        e?.stopPropagation();
                        handleRemove(index);
                      }}
                    />
                  </div>
                  <div className='border-t border-gray-300 my-1' />
                </div>
              ))}
            </div>
          </div>
        ) : (
          // Single file view
          <div className='flex flex-col items-center w-full'>
            <div className='mb-2 relative'>
              <img
                src={getFileAvatar(
                  selectedFiles[0]?.type,
                  selectedFiles[0]?.name,
                )}
                alt='file-icon'
                className='w-8 h-8'
              />
              <div className='absolute right-[-7px] top-[-5px] z-10 bg-white flex items-center justify-center w-[18px] h-[18px] rounded-full border text-red-base hover:bg-red-base hover:text-white'>
                <MaterialIcon
                  icon='close'
                  classes={['text-inherit', 'text-tiny']}
                  onClick={() => handleRemove(0)}
                />
              </div>
            </div>

            <div className='text-center'>
              <p className='text-pico font-medium text-gray-600 break-words'>
                {selectedFiles[0]?.name}
              </p>
              <p className='text-pico text-gray-500'>
                {selectedFiles[0] && formatFileSize(selectedFiles[0].size)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Drag Overlay */}
      {isDragActive && !hasFiles && (
        <div className='absolute inset-0 flex items-center justify-center bg-primary-50 bg-opacity-90 rounded-md border-2 border-primary-base border-dashed pointer-events-none'>
          <div className='flex flex-col justify-center align-center w-full'>
            <p className='text-primary-base font-medium text-xs text-center'>
              {t('data-sources.upload.drop-file-here')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
